import { MapPin, ShieldAlert, Sparkles } from "lucide-react";

import { NotesPanel } from "@/components/NotesPanel";
import { pickText, visualPolicyCopy } from "@/lib/content";

export const EntryDetailPanel = ({ entry, language, showNotes = true }) => {
  if (!entry) {
    return (
      <div className="glass-card h-full rounded-[2rem] p-6 md:p-8" data-testid="entry-detail-empty-state">
        <p className="section-label" data-testid="entry-detail-empty-eyebrow">
          {pickText(language, "Select a node", "Seleziona un nodo")}
        </p>
        <h3 className="mt-3 text-3xl text-foreground" data-testid="entry-detail-empty-title">
          {pickText(language, "Open the atlas to reveal the correspondence.", "Apri l'atlante per rivelare la corrispondenza.")}
        </h3>
        <p className="body-copy mt-4" data-testid="entry-detail-empty-description">
          {pickText(
            language,
            "Each point of the body unlocks a layered relation between metaphysical systems, body regions, and symbolic functions.",
            "Ogni punto del corpo sblocca una relazione stratificata tra sistemi metafisici, regioni corporee e funzioni simboliche.",
          )}
        </p>
      </div>
    );
  }

  const visualPolicy = visualPolicyCopy[language][entry.visual_policy] || visualPolicyCopy[language].standard;

  return (
    <div className="glass-card h-full space-y-6 rounded-[2rem] p-6 md:p-8" data-testid="entry-detail-panel">
      <div className="space-y-3" data-testid="entry-detail-header">
        <p className="section-label" data-testid="entry-detail-category">
          {entry.category}
        </p>
        <h3 className="text-4xl text-foreground" data-testid="entry-detail-title">
          {pickText(language, entry.title_en, entry.title_it)}
        </h3>
        <p className="text-sm uppercase tracking-[0.24em] text-primary/80" data-testid="entry-detail-subtitle">
          {pickText(language, entry.subtitle_en, entry.subtitle_it)}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="entry-detail-location-card">
          <div className="flex items-center gap-2 text-primary">
            <MapPin className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em]">{pickText(language, "Location", "Posizione")}</span>
          </div>
          <p className="mt-3 text-sm leading-7 text-slate-200" data-testid="entry-detail-location-text">
            {pickText(language, entry.location_en, entry.location_it)}
          </p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="entry-detail-function-card">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.24em]">{pickText(language, "Function", "Funzione")}</span>
          </div>
          <p className="mt-3 text-sm leading-7 text-slate-200" data-testid="entry-detail-function-text">
            {pickText(language, entry.function_en, entry.function_it)}
          </p>
        </div>
      </div>

      <div className="space-y-4" data-testid="entry-detail-description-block">
        <p className="body-copy" data-testid="entry-detail-description-text">
          {pickText(language, entry.description_en, entry.description_it)}
        </p>
        <div className="flex flex-wrap gap-2" data-testid="entry-detail-correspondences">
          {entry.correspondences.map((correspondence) => (
            <span
              className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-xs uppercase tracking-[0.2em] text-primary"
              data-testid={`entry-detail-correspondence-${correspondence.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              key={correspondence}
            >
              {correspondence}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-black/20 p-4" data-testid="entry-detail-visual-policy-card">
        <div className="flex items-center gap-2 text-primary">
          <ShieldAlert className="h-4 w-4" />
          <span className="text-xs uppercase tracking-[0.24em]">{pickText(language, "Representation", "Rappresentazione")}</span>
        </div>
        <p className="mt-3 text-sm leading-7 text-slate-300" data-testid="entry-detail-visual-policy-text">
          {visualPolicy}
        </p>
      </div>

      {showNotes ? <NotesPanel itemSlug={entry.slug} language={language} /> : null}
    </div>
  );
};